// ========================
// IRONPACT — Shared Types
// ========================

// === Hex Coordinates (Axial) ===
export interface HexCoord {
  q: number;
  r: number;
}

export function hexKey(q: number, r: number): string {
  return `${q},${r}`;
}

export function parseHexKey(key: string): HexCoord {
  const [q, r] = key.split(",").map(Number);
  return { q, r };
}

// === Terrain & Biomes ===
export type Biome = "ocean" | "coast" | "desert" | "plains" | "grassland" | "forest" | "mountain" | "tundra" | "swamp" | "volcanic";

export interface HexTile {
  q: number;
  r: number;
  biome: Biome;
  elevation: number;    // 0-1
  moisture: number;     // 0-1
  resource?: ResourceDeposit;
  ownerId?: string;     // player who owns this hex
  buildings: Building[];
  troops?: number;       // garrison count
}

// === Resources ===
export type ResourceType =
  | "money" | "steel" | "fuel"               // Tier 1 — Raw
  | "electronics" | "composites" | "munitions" // Tier 2 — Processed
  | "uranium" | "intelData";                   // Tier 3 — Strategic

export interface Resources {
  money: number;
  steel: number;
  fuel: number;
  electronics: number;
  composites: number;
  munitions: number;
  uranium: number;
  rareEarth: number;
  intelData: number;
}

export const EMPTY_RESOURCES: Resources = {
  money: 0, steel: 0, fuel: 0,
  electronics: 0, composites: 0, munitions: 0,
  uranium: 0, rareEarth: 0, intelData: 0,
};

export interface ResourceDeposit {
  type: "steel" | "fuel" | "uranium" | "composites" | "rareEarth";
  richness: number; // 0.5 - 2.0 multiplier
}

// === Buildings ===
export type BuildingType =
  | "mine" | "oilWell" | "factory" | "munitionsPlant" | "powerPlant"
  | "researchLab" | "tradePort" | "barracks"
  | "radarStation" | "missileSilo" | "antiMissileBattery" | "bunker"
  | "uraniumExtractor" | "rareEarthMine" | "compositesPlant" | "marketplace"
  | "spyAcademy";

export interface Building {
  type: BuildingType;
  level: number;
  hexQ: number;
  hexR: number;
  ownerId: string;
  completedAt?: number;   // timestamp when built
  disabledUntil?: number;  // cyber warfare: building disabled until this timestamp
  producing?: Production;
}

export interface Production {
  type: string;           // what's being produced
  startedAt: number;
  completesAt: number;
}

// === Military ===
export type MissileType = "tactical" | "cruise" | "icbm" | "stealth" | "nuclear";

export interface Missile {
  type: MissileType;
  ownerId: string;
  storedAt: HexCoord;   // silo location
}

export interface MissileLaunch {
  id: string;
  type: MissileType;
  launcherId?: string;   // player who launched
  fromHex: HexCoord;
  targetHex: HexCoord;
  launchedAt: number;
  eta: number;           // arrival timestamp
  intercepted?: boolean;
}

export type DefenseType = "basicBattery" | "advancedBattery" | "missileShield" | "thaad";

export const INTERCEPT_RATES: Record<DefenseType, number> = {
  basicBattery: 0.4,
  advancedBattery: 0.65,
  missileShield: 0.85,
  thaad: 0.5,            // specifically vs ICBMs
};

export const MISSILE_CONFIG: Record<MissileType, {
  range: number;        // hex range (-1 = unlimited)
  damage: number;
  buildTime: number;    // seconds
  cost: Partial<Resources>;
}> = {
  tactical:  { range: 3,  damage: 20,  buildTime: 1800,   cost: { munitions: 10, fuel: 5 } },
  cruise:    { range: 8,  damage: 50,  buildTime: 7200,   cost: { munitions: 25, fuel: 15, electronics: 10 } },
  icbm:      { range: -1, damage: 100, buildTime: 43200,  cost: { munitions: 50, fuel: 30, electronics: 25 } },
  stealth:   { range: 6,  damage: 50,  buildTime: 21600,  cost: { munitions: 30, fuel: 20, electronics: 30, composites: 15 } },
  nuclear:   { range: -1, damage: 500, buildTime: 259200, cost: { munitions: 100, fuel: 50, electronics: 50, uranium: 20 } },
};

// === Diplomacy ===
export type DiplomacyAction =
  | "denounce" | "proposeAlliance" | "tradeAgreement"
  | "nonAggressionPact" | "ceasefire" | "sanctions"
  | "declareWar" | "peaceTreaty";

export type DiplomacyStatus = "neutral" | "allied" | "war" | "nap" | "tradePartner" | "denounced";

export interface Treaty {
  id: string;
  type: DiplomacyAction;
  playerA: string;
  playerB: string;
  createdAt: number;
  expiresAt?: number;
  terms?: Record<string, any>;
}

// === Fog of War ===
export type Visibility = 0 | 1 | 2; // 0=unexplored, 1=explored, 2=visible

// === Player ===
export type GovernmentStance = "military" | "trade" | "surveillance" | "fortified" | "expansionist";

export interface Player {
  id: string;
  name: string;
  color: string;         // hex color for territory
  resources: Resources;
  reputation: number;    // 0-100
  capitalHex: HexCoord;
  techResearched: string[];
  stance?: GovernmentStance;
  stability?: number;
  spies?: Spy[];
  politicalWill?: number;
  stanceChangedAt?: number;
  online: boolean;
  lastSeen: number;
  dormancyStatus?: DormancyStatus;
  returnPeaceUntil?: number;  // timestamp when return peace window expires
  commanders?: Commander[];
}

// === Dormancy System ===
export type DormancyStatus = "active" | "dormant" | "deep_dormant" | "abandoned";

export const DORMANCY_THRESHOLDS = {
  DORMANT_MS: 24 * 60 * 60 * 1000,        // 24h -> dormant
  DEEP_DORMANT_MS: 72 * 60 * 60 * 1000,   // 72h -> deep dormant
  ABANDONED_MS: 7 * 24 * 60 * 60 * 1000,  // 7 days -> abandoned
  RETURN_PEACE_MS: 2 * 60 * 60 * 1000,    // 2h peace window on return
};

export const DORMANCY_EFFECTS = {
  // Dormant (24-72h): attackers deal 25% less damage
  DORMANT_DAMAGE_REDUCTION: 0.25,
  // Deep Dormant (72h-7d): no attacks allowed, territory frozen
  DEEP_DORMANT_IMMUNE: true,
  // Abandoned (7d+): territory claimable at 1.5x cost
  ABANDONED_CLAIM_MULTIPLIER: 1.5,
  // Abandoned: buildings decay 3% HP per day (future)
  ABANDONED_DECAY_PER_DAY: 0.03,
};

// === War Strain System ===
export interface WarStrain {
  attackerId: string;
  defenderId: string;
  attackerStrain: number;   // 0-100
  defenderStrain: number;   // 0-100
  warStartedAt: number;     // timestamp
  lastTickAt: number;       // last hourly tick
  attackerMissileHits: number;
  defenderMissileHits: number;
  attackerTroopsLost: number;
  defenderTroopsLost: number;
}

export const WAR_STRAIN_CONFIG = {
  HOURLY_BASE: 2,                    // +2 strain per hour of war
  PER_MISSILE_HIT: 5,                // +5 per missile hitting your territory
  PER_TROOP_KILLED: 0.3,             // +0.3 per friendly troop killed
  PER_HEX_CAPTURED: -1,              // -1 per hour per enemy hex you hold
  ATTACKER_MULTIPLIER: 1.25,         // attackers accumulate 25% faster
  // Thresholds
  LOW_THRESHOLD: 26,                 // 26-50: -10% production
  MID_THRESHOLD: 51,                 // 51-75: -25% production, 85% troop effectiveness
  HIGH_THRESHOLD: 76,                // 76-100: -40% production, ceasefire window
  MAX_STRAIN: 100,
  // Effects
  LOW_PRODUCTION_PENALTY: 0.10,      // -10%
  MID_PRODUCTION_PENALTY: 0.25,      // -25%
  MID_TROOP_PENALTY: 0.85,           // 85% effectiveness
  HIGH_PRODUCTION_PENALTY: 0.40,     // -40%
  // Peace dividend
  PEACE_DIVIDEND_THRESHOLD: 50,      // sign peace under 50 strain
  PEACE_DIVIDEND_BONUS: 0.15,        // +15% production
  PEACE_DIVIDEND_DURATION_MS: 24 * 60 * 60 * 1000, // 24 hours
};

// === Tech Tree ===
export type TechBranch = "military" | "defense" | "economy" | "intelligence";

export interface TechNode {
  id: string;
  branch: TechBranch;
  name: string;
  tier: number;          // 1-5
  researchTime: number;  // seconds
  cost: Partial<Resources>;
  requires?: string[];   // prerequisite tech IDs
  unlocks?: string;      // what this tech enables
  description: string;   // human-readable effect
}

// === Socket Events ===
export const SOCKET_EVENTS = {
  // Client → Server
  JOIN_GAME: "join-game",
  BUILD: "build",
  CANCEL_BUILD: "cancel-build",
  LAUNCH_MISSILE: "launch-missile",
  RECON_PING: "recon-ping",
  DIPLOMACY: "diplomacy",
  TRADE_OFFER: "trade-offer",
  TRADE_RESPOND: "trade-respond",
  RESEARCH: "start-research",
  CLAIM_HEX: "claim-hex",
  SPY_MISSION: "spy-mission",
  CHAT: "chat",

  // Server → Client
  GAME_STATE: "game-state",         // full state on join
  STATE_DELTA: "state-delta",       // incremental updates
  MISSILE_INCOMING: "missile-incoming",
  MISSILE_IMPACT: "missile-impact",
  WAR_DECLARED: "war-declared",
  RECON_ALERT: "recon-alert",       // someone scanned your territory
  RECON_RESULT: "recon-result",     // your scan results
  SPY_RESULT: "spy-result",
  DIPLOMACY_EVENT: "diplomacy-event",
  TRADE_INCOMING: "trade-incoming",
  PRODUCTION_COMPLETE: "production-complete",
  RESEARCH_COMPLETE: "research-complete",
  CHAT_MESSAGE: "chat-message",
  PLAYER_JOINED: "player-joined",
  PLAYER_LEFT: "player-left",
  EVENT_FEED: "event-feed",
  TRAIN_TROOPS: "train-troops",
  MOVE_TROOPS: "move-troops",
  ATTACK_HEX: "attack-hex",
  BATTLE_RESULT: "battle-result",
} as const;

// === Game Constants ===

// === Province Stability ===
export type StabilityBand = "thriving" | "stable" | "strained" | "unstable" | "collapse";

export interface StabilityConfig {
  // Band thresholds
  THRIVING_MIN: number;
  STABLE_MIN: number;
  STRAINED_MIN: number;
  UNSTABLE_MIN: number;
  // Effects per band
  bands: Record<StabilityBand, {
    productionMult: number;
    defenseMult: number;
    label: string;
    emoji: string;
  }>;
  // Tick adjustments (per hour)
  BASE_DECAY: number;           // natural decay per hour
  PEACE_RECOVERY: number;       // recovery when not at war
  GARRISON_BONUS: number;       // per 10 troops stationed empire-wide
  BUILDING_BONUS: number;       // per 5 buildings
  OVEREXPANSION_PENALTY: number; // per hex over threshold
  OVEREXPANSION_THRESHOLD: number; // hexes per building before penalty kicks in
  MISSILE_HIT_PENALTY: number;  // per missile impact
  WAR_PENALTY: number;          // per active war
  REVOLT_CHANCE: number;        // chance per tick at Collapse (0-1)
}


// === World Projects (Wonders) ===
export type WorldProjectId = "commandNexus" | "industrialMegaplex" | "ironCitadel" | "broadcastTower" | "strategicVault" | "deepStrikeSilo" | "uraniumComplex";

export interface WorldProjectConfig {
  id: WorldProjectId;
  name: string;
  emoji: string;
  description: string;
  effect: string;
  requiresBiome?: Biome[];
  requiresAdjacentResource?: string;  // e.g. "uranium"
  requiresTech: string[];
  cost: Partial<Resources>;
  buildTimeHours: number;
}

export const WORLD_PROJECTS: Record<WorldProjectId, WorldProjectConfig> = {
  commandNexus: {
    id: "commandNexus", name: "Command Nexus", emoji: "🏛️",
    description: "Central intelligence hub",
    effect: "+2 radar range on all stations, +15% spy success",
    requiresTech: ["mil3", "int2"],
    cost: { steel: 2000, electronics: 500 },
    buildTimeHours: 48,
  },
  industrialMegaplex: {
    id: "industrialMegaplex", name: "Industrial Megaplex", emoji: "🏭",
    description: "Massive production complex",
    effect: "+40% production to all buildings within 3 hexes",
    requiresBiome: ["plains", "mountain"],
    requiresTech: ["eco2", "eco3"],
    cost: { steel: 1500, composites: 300 },
    buildTimeHours: 36,
  },
  ironCitadel: {
    id: "ironCitadel", name: "Iron Citadel", emoji: "🏰",
    description: "Impenetrable fortress",
    effect: "85% intercept rate in province, must be destroyed before capture",
    requiresBiome: ["mountain"],
    requiresTech: ["def3", "def4"],
    cost: { composites: 2000, munitions: 500 },
    buildTimeHours: 48,
  },
  broadcastTower: {
    id: "broadcastTower", name: "Global Broadcast Tower", emoji: "📡",
    description: "World-wide intelligence network",
    effect: "Reveals all diplomacy, war declarations, and reputation changes",
    requiresTech: ["eco3", "int1"],
    cost: { electronics: 1000 },
    buildTimeHours: 24,
  },
  strategicVault: {
    id: "strategicVault", name: "Strategic Reserve Vault", emoji: "🏦",
    description: "Fortified resource stockpile",
    effect: "2x resource storage cap, immune to economic sanctions",
    requiresTech: ["eco2"],
    cost: { steel: 500, fuel: 500 },
    buildTimeHours: 20,
  },
  deepStrikeSilo: {
    id: "deepStrikeSilo", name: "Deep Strike Silo", emoji: "🚀",
    description: "Advanced ICBM launch facility",
    effect: "ICBMs travel 3x speed, cannot be destroyed by conventional missiles",
    requiresBiome: ["desert", "plains"],
    requiresTech: ["mil4"],
    cost: { munitions: 1000, electronics: 500 },
    buildTimeHours: 60,
  },
  uraniumComplex: {
    id: "uraniumComplex", name: "Uranium Enrichment Complex", emoji: "☢️",
    description: "Nuclear fuel processing plant",
    effect: "Halves nuclear build time, +0.5 uranium/hour passive",
    requiresAdjacentResource: "uranium",
    requiresTech: ["mil4"],
    cost: { uranium: 500, electronics: 1000 },
    buildTimeHours: 72,
  },
};

export interface WorldProjectState {
  id: WorldProjectId;
  hexQ: number;
  hexR: number;
  ownerId: string;
  startedAt: number;
  completedAt: number;
  completed: boolean;
}


// === World Chronicle & Milestones ===
export type MilestoneId = "firstWar" | "firstNuke" | "firstWonder" | "firstAlliance" | "firstRecon" | "firstTrade" | "territory50" | "territory100" | "tenTroops" | "fiftyTroops";

export interface MilestoneConfig {
  id: MilestoneId;
  name: string;
  emoji: string;
  description: string;
}

export const MILESTONES: Record<MilestoneId, MilestoneConfig> = {
  firstWar:      { id: "firstWar",      name: "First Blood",        emoji: "🔥", description: "First player to declare war" },
  firstNuke:     { id: "firstNuke",     name: "Nuclear Dawn",       emoji: "☢️", description: "First nuclear strike" },
  firstWonder:   { id: "firstWonder",   name: "Wonder Builder",     emoji: "🏗️", description: "First to complete a World Project" },
  firstAlliance: { id: "firstAlliance", name: "Diplomat",           emoji: "🤝", description: "First alliance formed" },
  firstRecon:    { id: "firstRecon",    name: "All-Seeing Eye",     emoji: "📡", description: "First recon ping" },
  firstTrade:    { id: "firstTrade",    name: "Market Maker",       emoji: "💰", description: "First trade completed" },
  territory50:   { id: "territory50",   name: "Land Baron",         emoji: "🗺️", description: "First to control 50 hexes" },
  territory100:  { id: "territory100",  name: "Empire Builder",     emoji: "👑", description: "First to control 100 hexes" },
  tenTroops:     { id: "tenTroops",     name: "Commander",          emoji: "⚔️", description: "First to train 10 troops" },
  fiftyTroops:   { id: "fiftyTroops",   name: "Warlord",            emoji: "🎖️", description: "First to have 50 troops" },
};

export interface ChronicleEntry {
  day: number;        // game day (from world creation)
  timestamp: number;
  type: string;
  message: string;
  playerId?: string;
  playerName?: string;
  milestone?: MilestoneId;
}


// === Named Spies & Espionage ===
export type SpyMissionType = "gatherIntel" | "stealTech" | "sabotage" | "sowDissent" | "counterIntel";

export interface SpyMissionConfig {
  id: SpyMissionType;
  name: string;
  emoji: string;
  description: string;
  durationHours: number;
  successBase: number;    // base success rate 0-1
  catchRisk: number;      // base risk of being caught 0-1
}

export const SPY_MISSIONS: Record<SpyMissionType, SpyMissionConfig> = {
  gatherIntel: {
    id: "gatherIntel", name: "Gather Intel", emoji: "🔍",
    description: "Reveal target's resources, buildings, and troop counts",
    durationHours: 2, successBase: 0.75, catchRisk: 0.10,
  },
  stealTech: {
    id: "stealTech", name: "Steal Technology", emoji: "🔬",
    description: "Copy one of target's researched techs",
    durationHours: 6, successBase: 0.40, catchRisk: 0.25,
  },
  sabotage: {
    id: "sabotage", name: "Sabotage Production", emoji: "💥",
    description: "Reduce target's production by 25% for 4 hours",
    durationHours: 4, successBase: 0.50, catchRisk: 0.30,
  },
  sowDissent: {
    id: "sowDissent", name: "Sow Dissent", emoji: "📢",
    description: "Reduce target's Province Stability by 15",
    durationHours: 8, successBase: 0.35, catchRisk: 0.35,
  },
  counterIntel: {
    id: "counterIntel", name: "Counter-Intelligence", emoji: "🛡️",
    description: "Defend against enemy spies for 12 hours (+30% catch rate)",
    durationHours: 12, successBase: 0.90, catchRisk: 0.0,
  },
};

export interface Spy {
  id: string;
  name: string;
  ownerId: string;
  level: number;          // 1-5, increases success rate
  status: "idle" | "mission" | "captured" | "dead";
  currentMission?: SpyMissionType;
  missionTarget?: string; // target player id
  missionEndAt?: number;
  createdAt: number;
}

export const SPY_NAMES = [
  "Shadow", "Fox", "Viper", "Ghost", "Raven", "Phantom", "Wolf", "Cobra",
  "Hawk", "Specter", "Jackal", "Lynx", "Osprey", "Falcon", "Mantis",
  "Scorpion", "Panther", "Owl", "Serpent", "Wraith",
];

export const SPY_CONFIG = {
  TRAIN_COST: { money: 200, intelData: 50 } as Partial<Resources>,
  TRAIN_TIME_MS: 120_000,   // 2 minutes
  MAX_SPIES: 5,
  LEVEL_UP_MISSIONS: 3,     // missions completed to level up
  LEVEL_SUCCESS_BONUS: 0.08, // +8% success per level above 1
  COUNTER_INTEL_BONUS: 0.30, // +30% catch rate when counter-intel active
};


// === World Events (Chaos Engine) ===
export type WorldEventId = "drought" | "resourceBoom" | "banditSurge" | "plague" | "goldenAge" | "meteorStrike" | "tradeWinds" | "volcanicEruption" | "diplomaticCrisis" | "techBreakthrough";

export interface WorldEventConfig {
  id: WorldEventId;
  name: string;
  emoji: string;
  description: string;
  durationHours: number;
  effect: string;
  friction: boolean;  // true = designed to create conflict
}

export const WORLD_EVENTS: Record<WorldEventId, WorldEventConfig> = {
  drought: {
    id: "drought", name: "Drought", emoji: "🏜️",
    description: "Water sources dry up across a region",
    effect: "Fuel production -20% in desert hexes for 24h",
    durationHours: 24, friction: true,
  },
  resourceBoom: {
    id: "resourceBoom", name: "Resource Boom", emoji: "💎",
    description: "Rich deposits discovered in a random region",
    effect: "Random resource +30% production for 12h in affected area",
    durationHours: 12, friction: true,
  },
  banditSurge: {
    id: "banditSurge", name: "Bandit Surge", emoji: "🏴‍☠️",
    description: "Bandits raid border territories",
    effect: "Ungarrisoned border hexes lose 1 building level",
    durationHours: 0, friction: true,
  },
  plague: {
    id: "plague", name: "Plague", emoji: "🦠",
    description: "Disease spreads through densely built areas",
    effect: "All players: -10 stability, -15% production for 8h",
    durationHours: 8, friction: false,
  },
  goldenAge: {
    id: "goldenAge", name: "Golden Age", emoji: "✨",
    description: "Prosperity sweeps the continent",
    effect: "All players: +10 stability, +20% money for 12h",
    durationHours: 12, friction: false,
  },
  meteorStrike: {
    id: "meteorStrike", name: "Meteor Strike", emoji: "☄️",
    description: "A meteor impacts unclaimed territory, revealing rare resources",
    effect: "Creates 3 rare earth deposits in unclaimed area",
    durationHours: 0, friction: true,
  },
  tradeWinds: {
    id: "tradeWinds", name: "Trade Winds", emoji: "🌊",
    description: "Favorable conditions boost maritime trade",
    effect: "Trade Port income doubled for 12h",
    durationHours: 12, friction: false,
  },
  volcanicEruption: {
    id: "volcanicEruption", name: "Volcanic Eruption", emoji: "🌋",
    description: "A volcanic hex erupts, damaging nearby buildings",
    effect: "Volcanic hex + neighbors: buildings damaged, rare earth revealed",
    durationHours: 0, friction: true,
  },
  diplomaticCrisis: {
    id: "diplomaticCrisis", name: "Diplomatic Crisis", emoji: "⚠️",
    description: "A border dispute escalates tensions",
    effect: "Two random adjacent empires get -20 reputation with each other",
    durationHours: 0, friction: true,
  },
  techBreakthrough: {
    id: "techBreakthrough", name: "Tech Breakthrough", emoji: "🔬",
    description: "Scientific discovery benefits all",
    effect: "All players: current research 25% faster for 12h",
    durationHours: 12, friction: false,
  },
};

export const CHAOS_ENGINE_CONFIG = {
  MIN_INTERVAL_MS: 2 * 60 * 60 * 1000,    // min 2h between events
  MAX_INTERVAL_MS: 6 * 60 * 60 * 1000,    // max 6h between events
  PEACE_TRIGGER_MS: 72 * 60 * 60 * 1000,  // 72h peace = force friction event
  FRICTION_WEIGHT: 0.6,                     // 60% chance of friction event when forced
};

export const STABILITY_CONFIG: StabilityConfig = {
  THRIVING_MIN: 80,
  STABLE_MIN: 50,
  STRAINED_MIN: 30,
  UNSTABLE_MIN: 15,
  bands: {
    thriving:  { productionMult: 1.20, defenseMult: 1.15, label: "Thriving",  emoji: "🟢" },
    stable:    { productionMult: 1.00, defenseMult: 1.00, label: "Stable",    emoji: "🔵" },
    strained:  { productionMult: 0.85, defenseMult: 0.95, label: "Strained",  emoji: "🟡" },
    unstable:  { productionMult: 0.70, defenseMult: 0.85, label: "Unstable",  emoji: "🟠" },
    collapse:  { productionMult: 0.50, defenseMult: 0.70, label: "Collapse",  emoji: "🔴" },
  },
  BASE_DECAY: -0.5,
  PEACE_RECOVERY: 2.0,
  GARRISON_BONUS: 0.5,
  BUILDING_BONUS: 0.3,
  OVEREXPANSION_PENALTY: -0.3,
  OVEREXPANSION_THRESHOLD: 3,  // 3 hexes per building before penalty
  MISSILE_HIT_PENALTY: -5,
  WAR_PENALTY: -1.5,
  REVOLT_CHANCE: 0.15,
};

export function getStabilityBand(stability: number): StabilityBand {
  if (stability >= 80) return "thriving";
  if (stability >= 50) return "stable";
  if (stability >= 30) return "strained";
  if (stability >= 15) return "unstable";
  return "collapse";
}

export const GAME_CONSTANTS = {
  TICK_INTERVAL_MS: 10_000,          // 10 second game tick
  RESOURCE_TICK_MS: 30_000,
  // Geographic Destiny — terrain-locked resource distribution
  TERRAIN_RESOURCES: {
    mountain: ["steel", "rareEarth"],
    desert: ["fuel"],
    coast: ["fuel"],
    forest: ["composites"],
    swamp: ["composites"],
    plains: ["steel"],        // sparse
    volcanic: ["rareEarth"],  // rare biome
    tundra: [],               // uranium placed manually
    grassland: [],            // fertile but no deposits
    ocean: [],
  } as Record<string, string[]>,          // resource production every 60s
  MAX_PLAYERS: 15,
  MIN_PLAYERS: 2,
  MAP_RADIUS: 60,
  URANIUM_DEPOSITS: 5,                    // hex radius from center
  STARTING_HEXES: 7,                 // hexes per player at start
  STARTING_RESOURCES: {
    money: 1500, steel: 600, fuel: 400,
    electronics: 0, composites: 0, munitions: 0,
    uranium: 0, rareEarth: 0, intelData: 0,
  } as Resources,
  WAR_PREP_TIME_MS: 2 * 60 * 60 * 1000,  // 2 hours
  CEASEFIRE_COOLDOWN_MS: 60 * 60 * 1000,  // 1 hour
  NEWBIE_SHIELD_MS: 24 * 60 * 60 * 1000,  // 24 hours
  REPUTATION_RECOVERY_PER_DAY: 2,
  RECON_BASE_COST: 100,              // money
  RECON_DISTANCE_MULT: 20,           // cost per hex distance
  RECON_REVEAL_DURATION_MS: 60_000,  // 60 seconds
  TROOP_COST: 50,                    // money per troop
  TROOP_TRAIN_TIME_MS: 30_000,       // 30s per troop
  TROOP_MOVE_TIME_MS: 5_000,         // 5s per hex
  TROOP_ATTACK_BONUS: 0.1,           // +10% per extra troop
  TROOP_DEFENSE_BONUS: 0.2,          // +20% defender advantage
  BARRACKS_CAPACITY: 20,             // max troops per barracks hex
  TROOP_UPKEEP_PER_MIN: 1,          // money per troop per minute
};
// === Government Stances ===
export interface StanceConfig {
  id: GovernmentStance;
  name: string;
  icon: string;
  description: string;
  bonuses: string[];
  penalties: string[];
  effects: {
    missileProductionSpeed?: number;
    troopTrainSpeed?: number;
    portIncome?: number;
    tradeFeeReduction?: number;
    spySuccess?: number;
    counterIntel?: number;
    interceptBonus?: number;
    stabilityRecovery?: number;
    claimCostMult?: number;
    captureStabilityBonus?: number;
    productionMult?: number;
    militaryProductionMult?: number;
    expansionSpeedMult?: number;
    interceptRateMult?: number;
  };
}

export const STANCE_CONFIGS: Record<GovernmentStance, StanceConfig> = {
  military: {
    id: "military", name: "Military State", icon: "⚔️",
    description: "War machine. Faster missiles and stronger troops, weaker economy.",
    bonuses: ["+25% missile build speed", "+15% troop training speed"],
    penalties: ["-20% economic production"],
    effects: { missileProductionSpeed: 1.25, troopTrainSpeed: 1.15, productionMult: 0.80 },
  },
  trade: {
    id: "trade", name: "Trade Empire", icon: "💰",
    description: "Commerce flows. Better port income and trade fees, weaker military.",
    bonuses: ["+30% port income", "-15% trade fees"],
    penalties: ["-15% military production speed"],
    effects: { portIncome: 1.30, tradeFeeReduction: 0.85, militaryProductionMult: 0.85 },
  },
  surveillance: {
    id: "surveillance", name: "Surveillance State", icon: "👁️",
    description: "All-seeing eye. Superior espionage, slower stability recovery.",
    bonuses: ["+40% spy success", "+20% counter-intel"],
    penalties: ["-15% stability recovery"],
    effects: { spySuccess: 1.40, counterIntel: 1.20, stabilityRecovery: 0.85 },
  },
  fortified: {
    id: "fortified", name: "Fortified State", icon: "🛡️",
    description: "Impenetrable walls. Better intercept and stability, slower expansion.",
    bonuses: ["+25% intercept rate", "+20% stability recovery"],
    penalties: ["-20% expansion speed"],
    effects: { interceptRateMult: 1.25, stabilityRecovery: 1.20, expansionSpeedMult: 0.80 },
  },
  expansionist: {
    id: "expansionist", name: "Expansionist", icon: "🌍",
    description: "Manifest destiny. Cheaper territory claims, weaker defenses.",
    bonuses: ["-30% hex claim cost", "+15% capture stability"],
    penalties: ["-20% intercept rate"],
    effects: { claimCostMult: 0.70, captureStabilityBonus: 1.15, interceptRateMult: 0.80 },
  },
};

export const STANCE_CHANGE_COST = 10;
export const STANCE_CHANGE_COOLDOWN_MS = 4 * 60 * 60 * 1000;
export const POLITICAL_WILL_PER_HOUR = 1;
export const POLITICAL_WILL_MAX = 20;

// === Building Configs ===
export interface BuildingConfig {
  type: BuildingType;
  name: string;
  icon: string;
  description: string;
  buildTime: number;       // seconds
  cost: Partial<Resources>;
  production?: Partial<Resources>;  // per resource tick
  requiresBiome?: Biome[];
  requiresResource?: string;        // hex must have this resource deposit
  maxPerHex: number;
  visionRange?: number;    // hexes of vision this building provides
  requiresTech?: string;   // tech ID required to unlock this building
}

export const BUILDING_CONFIGS: Record<BuildingType, BuildingConfig> = {
  mine: {
    type: "mine", name: "Mine", icon: "\u26cf\ufe0f",
    description: "Extracts steel from deposits",
    buildTime: 180, cost: { money: 100 },
    production: { steel: 1 },
    requiresResource: "steel",
    maxPerHex: 1,
  },
  oilWell: {
    type: "oilWell", name: "Oil Well", icon: "\ud83d\udee2\ufe0f",
    description: "Extracts fuel from deposits",
    buildTime: 180, cost: { money: 100 },
    production: { fuel: 1 },
    requiresResource: "fuel",
    maxPerHex: 1,
  },
  factory: {
    type: "factory", name: "Factory", icon: "\ud83c\udfed",
    description: "Produces electronics from steel + fuel",
    buildTime: 1800, cost: { money: 300, steel: 50 },
    production: { electronics: 1 },
    maxPerHex: 1,
  },
  munitionsPlant: {
    type: "munitionsPlant", name: "Munitions Plant", icon: "\ud83d\udca3",
    description: "Produces munitions from steel + fuel",
    buildTime: 1800, cost: { money: 300, steel: 100, fuel: 50 },
    production: { munitions: 1 },
    maxPerHex: 1,
  },
  powerPlant: {
    type: "powerPlant", name: "Power Plant", icon: "\u26a1",
    description: "Boosts all production in adjacent hexes by 50%",
    buildTime: 2400, cost: { money: 500, steel: 100 },
    maxPerHex: 1,
  },
  researchLab: {
    type: "researchLab", name: "Research Lab", icon: "\ud83d\udd2c",
    description: "Required to research technologies",
    buildTime: 3600, cost: { money: 500, steel: 100, electronics: 50 },
    maxPerHex: 1,
  },
  tradePort: {
    type: "tradePort", name: "Trade Port", icon: "\ud83d\udea2",
    description: "Enables trade routes, generates passive income",
    buildTime: 120, cost: { money: 300, steel: 50 },
    production: { money: 3 },
    requiresBiome: ["coast"],
    maxPerHex: 1,
  },
  barracks: {
    type: "barracks", name: "Barracks", icon: "\ud83c\udff0",
    description: "Defends territory, required for ground units",
    buildTime: 3600, cost: { money: 200, steel: 50 },
    maxPerHex: 1,
    visionRange: 3,
  },
  radarStation: {
    type: "radarStation", name: "Radar Station", icon: "\ud83d\udce1",
    description: "Reveals nearby enemy activity",
    buildTime: 7200, cost: { money: 200, electronics: 30 },
    maxPerHex: 1, requiresTech: "int1",
    visionRange: 5,
  },
  missileSilo: {
    type: "missileSilo", name: "Missile Silo", icon: "\ud83d\ude80",
    description: "Stores and launches missiles",
    buildTime: 21600, cost: { money: 800, steel: 200, electronics: 50 },
    maxPerHex: 1, requiresTech: "mil2",
  },
  antiMissileBattery: {
    type: "antiMissileBattery", name: "Anti-Missile Battery", icon: "\ud83d\udee1\ufe0f",
    description: "Intercepts incoming missiles",
    buildTime: 14400, cost: { money: 500, steel: 100, electronics: 50 },
    maxPerHex: 1, requiresTech: "def2",
  },
  bunker: {
    type: "bunker", name: "Bunker", icon: "\ud83c\udfda\ufe0f",
    description: "Protects buildings from missile damage",
    buildTime: 3600, cost: { money: 300, steel: 150 },
    maxPerHex: 1, requiresTech: "def1",
  },
  spyAcademy: {
    type: "spyAcademy", name: "Spy Academy", icon: "\ud83d\udd75\ufe0f",
    description: "Trains spies for espionage missions",
    buildTime: 7200, cost: { money: 600, electronics: 50 },
    production: { intelData: 1 },
    maxPerHex: 1, requiresTech: "int2",
  },
  uraniumExtractor: {
    type: "uraniumExtractor", name: "Uranium Extractor", icon: "☢️",
    description: "Extracts uranium from deposits — handle with care",
    buildTime: 2400, cost: { money: 500, steel: 100, electronics: 50 },
    production: { uranium: 1 },
    requiresResource: "uranium", requiresTech: "mil2",
    maxPerHex: 1,
  },
  rareEarthMine: {
    type: "rareEarthMine", name: "Rare Earth Mine", icon: "💎",
    description: "Extracts rare earth minerals for advanced projects",
    buildTime: 150, cost: { money: 400, steel: 80 },
    production: { rareEarth: 1 },
    requiresResource: "rareEarth",
    maxPerHex: 1,
  },
  compositesPlant: {
    type: "compositesPlant", name: "Composites Plant", icon: "🧱",
    description: "Processes raw composites into usable materials",
    buildTime: 120, cost: { money: 250, steel: 50 },
    production: { composites: 2 },
    requiresResource: "composites",
    maxPerHex: 1,
  },
  marketplace: {
    type: "marketplace", name: "Marketplace", icon: "🏪",
    description: "Generates income from local commerce — build anywhere on land",
    buildTime: 1200, cost: { money: 150 },
    production: { money: 3 },
    requiresBiome: ["plains", "grassland", "desert", "forest", "mountain", "tundra", "swamp", "volcanic"],
    maxPerHex: 1,
  },
};


// === Building Upgrade System ===
export const MAX_BUILDING_LEVEL = 3;
export const UPGRADE_COST_MULT = [1, 2, 4];      // level 1=base, 2=2x, 3=4x
export const UPGRADE_TIME_MULT = [1, 1.5, 2];     // level 1=base, 2=1.5x, 3=2x
export const UPGRADE_OUTPUT_MULT = [1, 1.5, 2];   // level 1=base, 2=+50%, 3=+100%

// === TECH TREE ===

export const TECH_TREE: Record<string, TechNode> = {
  // Military branch
  "mil1": { id: "mil1", name: "Conscription", branch: "military", tier: 1,
    cost: { money: 500, steel: 200 }, researchTime: 300_000,
    description: "Troops train 50% faster (15s instead of 30s)" },
  "mil2": { id: "mil2", name: "Advanced Munitions", branch: "military", tier: 2,
    cost: { money: 1000, munitions: 300 }, researchTime: 7_200_000,
    requires: ["mil1"], description: "Troops deal 25% more damage, missiles build 30% faster. Unlocks: Missile Silo, Uranium Extractor" },
  "mil3": { id: "mil3", name: "ICBM Program", branch: "military", tier: 3,
    cost: { money: 2000, electronics: 500, fuel: 500 }, researchTime: 28_800_000,
    requires: ["mil2"], description: "Unlock ICBM missiles, all missile range +50%. Unlocks: Command Nexus (wonder)" },
  "mil4": { id: "mil4", name: "Nuclear Arsenal", branch: "military", tier: 4,
    cost: { money: 5000, uranium: 50, electronics: 1000 }, researchTime: 129_600_000,
    requires: ["mil3"], description: "Unlock nuclear missiles (ALERTS ALL PLAYERS). Unlocks: Deep Strike Silo, Uranium Complex (wonders)" },

  // Defense branch
  "def1": { id: "def1", name: "Fortification", branch: "defense", tier: 1,
    cost: { money: 400, steel: 300 }, researchTime: 300_000,
    description: "Garrison defense bonus +50% (up from 20%)" },
  "def2": { id: "def2", name: "Anti-Missile Battery", branch: "defense", tier: 2,
    cost: { money: 800, electronics: 200 }, researchTime: 7_200_000,
    requires: ["def1"], description: "Missile intercept rate 60% (up from 40%). Unlocks: Anti-Missile Battery" },
  "def3": { id: "def3", name: "Deep Bunkers", branch: "defense", tier: 3,
    cost: { money: 1500, steel: 500, composites: 200 }, researchTime: 28_800_000,
    requires: ["def2"], description: "Buildings survive missile hits (except nukes). Unlocks: Iron Citadel (wonder)" },
  "def4": { id: "def4", name: "Missile Shield", branch: "defense", tier: 4,
    cost: { money: 4000, electronics: 800, composites: 500 }, researchTime: 129_600_000,
    requires: ["def3"], description: "80% intercept rate for all incoming missiles" },

  // Economy branch
  "eco1": { id: "eco1", name: "Trade Routes", branch: "economy", tier: 1,
    cost: { money: 300 }, researchTime: 300_000,
    description: "Trade Ports produce +50% income" },
  "eco2": { id: "eco2", name: "Industrial Revolution", branch: "economy", tier: 2,
    cost: { money: 600, steel: 200 }, researchTime: 7_200_000,
    requires: ["eco1"], description: "All resource production +50%, buildings construct 30% faster" },
  "eco3": { id: "eco3", name: "Supply Chains", branch: "economy", tier: 3,
    cost: { money: 1200, electronics: 300 }, researchTime: 28_800_000,
    requires: ["eco2"], description: "All costs reduced 30% (troops, buildings, missiles)" },
  "eco4": { id: "eco4", name: "Economic Dominance", branch: "economy", tier: 4,
    cost: { money: 3000, intelData: 200 }, researchTime: 129_600_000,
    requires: ["eco3"], description: "Tax income tripled, claim hexes for free" },

  // Intelligence branch
  "int1": { id: "int1", name: "Field Agents", branch: "intelligence", tier: 1,
    cost: { money: 400, intelData: 50 }, researchTime: 300_000,
    description: "Recon reveals 3-hex radius (up from 2), lasts 90s" },
  "int2": { id: "int2", name: "Satellite Imagery", branch: "intelligence", tier: 2,
    cost: { money: 800, electronics: 300 }, researchTime: 7_200_000,
    requires: ["int1"], description: "Recon cost halved, unlock stealth missiles. Unlocks: Spy Academy" },
  "int3": { id: "int3", name: "Cyber Warfare", branch: "intelligence", tier: 3,
    cost: { money: 1500, intelData: 300, electronics: 400 }, researchTime: 28_800_000,
    requires: ["int2"], description: "Sabotage: disable enemy building for 2min" },
  "int4": { id: "int4", name: "Total Surveillance", branch: "intelligence", tier: 4,
    cost: { money: 4000, intelData: 500 }, researchTime: 129_600_000,
    requires: ["int3"], description: "See all enemy resource counts and troop totals" },
};
// === Commander / Hero System ===
export type CommanderSpecialty = "warfare" | "logistics" | "intelligence" | "fortification" | "diplomacy";

export interface CommanderAbility {
  id: string;
  name: string;
  description: string;
  specialty: CommanderSpecialty;
  minLevel: number;
}

export interface Commander {
  id: string;
  name: string;
  title: string;
  ownerId: string;
  specialty: CommanderSpecialty;
  level: number;           // 1-10
  xp: number;              // current XP toward next level
  assignedHex?: { q: number; r: number };  // null = reserve (HQ)
  status: "active" | "recovering" | "kia";
  abilities: string[];     // unlocked ability IDs
  stats: {
    attack: number;        // 0-100, base + level scaling
    defense: number;
    logistics: number;     // resource production bonus
    inspiration: number;   // troop morale / capacity bonus
  };
  combatsSurvived: number;
  killCount: number;
  recruitedAt: number;
  recoveryUntil?: number;  // timestamp when recovery ends
}

export const COMMANDER_NAMES = [
  "Marshal Kaine", "General Ashford", "Warden Creed", "Admiral Voss",
  "Colonel Draven", "Strategos Tal", "Commander Reis", "Centurion Block",
  "Field Marshal Quinn", "Brigadier Holt", "Captain Sable", "Warlord Thane",
  "Tactician Mercer", "Sentinel Rook", "Commander Stark", "Admiral Pierce",
  "General Korr", "Marshal Steele", "Colonel Zara", "Warden Falk",
];

export const COMMANDER_TITLES: Record<CommanderSpecialty, string[]> = {
  warfare:       ["Strike Lead", "Battle Commander", "War Marshal", "Grand Marshal", "Supreme Commander"],
  logistics:     ["Supply Officer", "Quartermaster", "Logistics Chief", "Operations Director", "Grand Architect"],
  intelligence:  ["Scout Lead", "Intel Officer", "Spymaster", "Shadow Director", "Ghost Commander"],
  fortification: ["Garrison Lead", "Fort Commander", "Bastion Chief", "Shield Marshal", "Iron Warden"],
  diplomacy:     ["Envoy", "Ambassador", "Chief Diplomat", "Foreign Minister", "Grand Chancellor"],
};

export const COMMANDER_ABILITIES: CommanderAbility[] = [
  // Warfare
  { id: "blitz", name: "Blitz Tactics", description: "+25% attack power for troops on this hex", specialty: "warfare", minLevel: 1 },
  { id: "veteranDrill", name: "Veteran Drill", description: "Troops train 40% faster", specialty: "warfare", minLevel: 3 },
  { id: "ironWill", name: "Iron Will", description: "Troops never retreat (fight to death)", specialty: "warfare", minLevel: 5 },
  { id: "supremeCommand", name: "Supreme Command", description: "+50% attack and defense for all troops in 3-hex radius", specialty: "warfare", minLevel: 8 },

  // Logistics
  { id: "efficientOps", name: "Efficient Operations", description: "+30% resource production on assigned hex", specialty: "logistics", minLevel: 1 },
  { id: "rapidBuild", name: "Rapid Construction", description: "Buildings built 50% faster on this hex", specialty: "logistics", minLevel: 3 },
  { id: "supplyLine", name: "Supply Line", description: "+15% production to all hexes within 2-hex radius", specialty: "logistics", minLevel: 5 },
  { id: "industrialGenius", name: "Industrial Genius", description: "+50% production empire-wide", specialty: "logistics", minLevel: 8 },

  // Intelligence
  { id: "reconMaster", name: "Recon Master", description: "Permanent vision +2 hex radius", specialty: "intelligence", minLevel: 1 },
  { id: "counterOps", name: "Counter Operations", description: "+40% chance to catch enemy spies", specialty: "intelligence", minLevel: 3 },
  { id: "saboteur", name: "Saboteur", description: "Cyber attacks cost 50% less", specialty: "intelligence", minLevel: 5 },
  { id: "omniscience", name: "Omniscience", description: "See all enemy troop movements in real-time", specialty: "intelligence", minLevel: 8 },

  // Fortification
  { id: "bunkerDown", name: "Bunker Down", description: "+30% defense for troops on this hex", specialty: "fortification", minLevel: 1 },
  { id: "missileBattery", name: "Enhanced Battery", description: "+20% missile intercept rate", specialty: "fortification", minLevel: 3 },
  { id: "citadel", name: "Citadel Protocol", description: "Hex cannot be claimed while troops present", specialty: "fortification", minLevel: 5 },
  { id: "aegisShield", name: "Aegis Shield", description: "90% intercept rate + reflect 25% damage", specialty: "fortification", minLevel: 8 },

  // Diplomacy
  { id: "tradeBonus", name: "Trade Envoy", description: "+20% trade deal value", specialty: "diplomacy", minLevel: 1 },
  { id: "peacekeep", name: "Peacekeeping", description: "War strain accumulates 30% slower", specialty: "diplomacy", minLevel: 3 },
  { id: "propaganda", name: "Propaganda", description: "Stability recovers 40% faster", specialty: "diplomacy", minLevel: 5 },
  { id: "hegemon", name: "Hegemonic Influence", description: "Can force NAP on any player (24h cooldown)", specialty: "diplomacy", minLevel: 8 },
];

export const COMMANDER_CONFIG = {
  RECRUIT_COST: { money: 500, steel: 100, electronics: 50 } as Partial<Resources>,
  MAX_COMMANDERS: 3,
  XP_PER_LEVEL: [0, 100, 250, 500, 800, 1200, 1700, 2300, 3000, 4000], // XP needed for levels 1-10
  XP_SOURCES: {
    COMBAT_WIN: 50,
    COMBAT_SURVIVE: 20,
    COMBAT_LOSS: 10,     // still get some XP for losing
    BUILDING_COMPLETE: 5,
    RESEARCH_COMPLETE: 15,
    SPY_SUCCESS: 25,
    TICK_ASSIGNED: 1,     // passive XP per resource tick while assigned
  },
  DEATH_CHANCE_BASE: 0.05,   // 5% chance of KIA when losing a battle
  DEATH_CHANCE_PER_TROOP_LOST: 0.02, // +2% per troop lost in battle
  RECOVERY_TIME_MS: 10 * 60 * 1000, // 10 min recovery after battle
  SPECIALTY_ICON: {
    warfare: "⚔️",
    logistics: "📦",
    intelligence: "🕵️",
    fortification: "🛡️",
    diplomacy: "🤝",
  } as Record<CommanderSpecialty, string>,
};
